import React from "react";
import logo from "../../assets/images/coreventum_logo.png";
import {
    SafetyCertificateFilled,
    InboxOutlined,
    LockFilled,
    TeamOutlined,
    RetweetOutlined,
    CalendarFilled,
    PhoneFilled,
    FileTextFilled,
    SecurityScanOutlined,
} from "@ant-design/icons";

const PrivacyPolicy = () => {
    return (
        <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 min-h-screen">
            {/* Header */}
            <header className="bg-white shadow-md px-6 py-4 flex flex-col md:flex-row items-center justify-between sticky top-0 z-50">
                <img src={logo} alt="Coreventum Logo" className="h-[50px] object-contain" />
            </header>

            {/* Hero Section */}
            <section className="w-full bg-gradient-to-r from-blue-200 via-white to-blue-200 py-20 shadow-sm">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <h1 className="text-3xl font-extrabold text-gray-800 mb-6 flex justify-center items-center gap-3">
                        <SafetyCertificateFilled className="text-blue-600 text-5xl" />
                        Coreventum Privacy Policy
                    </h1>
                    <p className="text-gray-500 text-lg italic mb-4">
                        Last Updated: 12 January 2024
                    </p>
                    <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                        Your intelligent Contract Lifecycle Management platform designed to
                        streamline how businesses create, negotiate, execute, and manage contracts.
                    </p>
                </div>
            </section>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 pb-12 mt-[-62px] space-y-10 text-gray-700 leading-relaxed">
                {/* ✅ OVERVIEW */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <SafetyCertificateFilled className="text-blue-600" />
                        OVERVIEW
                    </h2>
                    <p>
                        At Coreventum, we take your privacy seriously. This Privacy Policy
                        explains what information we collect, why we collect it, how we use
                        it, and how we protect it.
                    </p>
                    <p>
                        This policy applies to all users globally. While local laws may vary, we
                        strive to maintain consistent, high-standard privacy practices across all
                        regions.
                    </p>
                    <p>
                        This Privacy Policy is incorporated into and governed by our Terms of Service.
                    </p>
                </section>

                {/* 📥 INFORMATION WE COLLECT */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <InboxOutlined className="text-blue-600" />
                        INFORMATION WE COLLECT
                    </h2>
                    <p className="text-gray-600">
                        <strong>Why We Collect It</strong>
                        <br />
                        To deliver a seamless, secure, and personalized contract management
                        experience — improving features, ensuring security, providing support, and
                        complying with legal obligations.
                    </p>

                    <h3 className="font-semibold mt-4">1. Personally Identifiable Information (PII)</h3>
                    <ul className="list-disc ml-6">
                        <li>Register an account</li>
                        <li>Upload or draft contracts</li>
                        <li>Invite team members</li>
                        <li>Contact support</li>
                        <li>Integrate with third-party tools</li>
                    </ul>
                    <p className="mt-2">Examples: Full name, business email, job title, phone, IP address, etc.</p>

                    <h3 className="font-semibold mt-4">2. Non-Personally Identifiable Information (Non-PII)</h3>
                    <ul className="list-disc ml-6">
                        <li>Pages visited & time spent</li>
                        <li>Browser type, OS, resolution</li>
                        <li>Error logs & crash reports</li>
                    </ul>

                    <h3 className="font-semibold mt-4">3. Contract & Document Data</h3>
                    <ul className="list-disc ml-6">
                        <li>Contract text, clauses</li>
                        <li>Signatures, negotiation history</li>
                        <li>Internal comments, tags, reminders</li>
                    </ul>

                    <h3 className="font-semibold mt-4">4. Log Data & Cookies</h3>
                    <ul className="list-disc ml-6">
                        <li>IP address, browser, timestamps</li>
                        <li>Cookies for login, preferences, analytics</li>
                    </ul>
                </section>

                {/* 🔐 HOW WE USE */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <LockFilled className="text-blue-600" />
                        HOW WE USE YOUR INFORMATION
                    </h2>
                    <ul className="list-disc ml-6">
                        <li>Deliver and improve services</li>
                        <li>Respond to support requests</li>
                        <li>Send service-related emails</li>
                        <li>Personalize dashboard & recommendations</li>
                        <li>Comply with legal obligations</li>
                        <li>Prevent fraud and abuse</li>
                    </ul>
                    <p className="font-semibold mt-3">We do NOT sell your PII to third parties.</p>
                </section>

                {/* 🤝 SHARING */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <TeamOutlined className="text-blue-600" />
                        WHEN WE SHARE YOUR INFORMATION
                    </h2>
                    <p>We may share in limited cases:</p>
                    <ul className="list-disc ml-6">
                        <li>With your consent (e.g., inviting team members)</li>
                        <li>With service providers (hosting, monitoring)</li>
                        <li>For legal & safety requirements</li>
                        <li>During business transfers/mergers</li>
                    </ul>
                </section>

                {/* RIGHTS */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <RetweetOutlined className="text-blue-600" />
                        YOUR RIGHTS & CONTROLS
                    </h2>
                    <ul className="list-disc ml-6">
                        <li>Access, correct, or delete data</li>
                        <li>Export your data</li>
                        <li>Opt out of marketing</li>
                        <li>Withdraw consent</li>
                    </ul>
                    <p>
                        👉 Email us at:{" "}
                        <a href="mailto:support@coreventum.com" className="text-blue-600 underline">
                            support@coreventum.com
                        </a>
                    </p>
                </section>

                {/* 🛡️ DATA PROTECTION FOR SENSITIVE INFO */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <SecurityScanOutlined className="text-blue-600" />
                        DATA PROTECTION FOR SENSITIVE INFORMATION
                    </h2>
                    <p>
                        We implement strict technical and organizational safeguards to protect sensitive data,
                        including contract data and any Gmail information accessed through Google APIs.
                    </p>
                    <ul className="list-disc ml-6 mt-2">
                        <li>All Gmail data is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256).</li>
                        <li>Access to Gmail data is limited to the minimum scope necessary and only used to send or receive emails on your behalf.</li>
                        <li>We do not store Gmail message content beyond what is required to perform the requested service.</li>
                        <li>Gmail API access tokens are stored securely and revoked immediately upon disconnection or deletion.</li>
                        <li>All Gmail data access is logged, monitored, and periodically reviewed.</li>
                        <li>You may request deletion of Gmail-related data at any time by contacting <a href="mailto:support@coreventum.com" className="text-blue-600 underline">support@coreventum.com</a>.</li>
                        <li>This use of Google user data complies with the Google API Services User Data Policy, including the Limited Use requirements.</li>
                    </ul>
                </section>

                {/* CHANGES */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <CalendarFilled className="text-blue-600" />
                        CHANGES TO THIS POLICY
                    </h2>
                    <p>
                        We may update this Privacy Policy. Updated date will be reflected above.
                    </p>
                </section>

                {/* CONTACT */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <PhoneFilled className="text-blue-600" />
                        CONTACT US
                    </h2>
                    <p>
                        📧{" "}
                        <a href="mailto:support@coreventum.com" className="text-blue-600 underline">
                            support@coreventum.com
                        </a>{" "}
                        | 🕒 Response: 3 business days
                    </p>
                </section>

                {/* FINAL NOTE */}
                <section className="bg-white p-8 rounded-2xl border border-gray-300">
                    <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <FileTextFilled className="text-blue-600" />
                        FINAL NOTE
                    </h2>
                    <p>
                        At Coreventum, trust is built through transparency. We are committed to
                        protecting your data like it’s our own.
                    </p>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-white text-gray-500 text-center py-6 mt-10 shadow-inner">
                <p className="text-sm">
                    Copyright © {new Date().getFullYear()} CONNECTCOSMIC NETWORKS PRIVATE
                    LIMITED. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
